<template>
  <div class="orders-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content">

        <!-- Auto-refresh progress bar -->
        <div class="refresh-track">
          <div class="refresh-fill" :style="{ width: refreshPct + '%' }"></div>
        </div>

        <div class="orders-container">

          <!-- Top row -->
          <div class="top-row">
            <div class="title-block">
              <h1 class="page-title">🍽 Orders</h1>
              <span class="live-dot"></span>
              <span class="order-count">{{ filteredOrders.length }}</span>
            </div>
            <button class="refresh-btn" :class="{ spinning: loading }" @click="loadOrders">
              ↻
            </button>
          </div>

          <!-- Filter pills -->
          <div class="filter-row">
            <button
              v-for="tab in statusTabs"
              :key="tab.value"
              :class="['pill', { active: orderFilter === tab.value }]"
              @click="orderFilter = tab.value"
            >
              {{ tab.icon }} {{ tab.label }}
              <span v-if="tab.value !== 'all' && countByStatus(tab.value)" class="pill-count">
                {{ countByStatus(tab.value) }}
              </span>
            </button>
          </div>

          <!-- Loading -->
          <div v-if="loading && orders.length === 0" class="state-block">
            <div class="spinner"></div>
            <p>Loading orders…</p>
          </div>

          <!-- Empty -->
          <div v-else-if="filteredOrders.length === 0" class="state-block">
            <div class="empty-icon">📭</div>
            <p>No {{ orderFilter === 'all' ? '' : orderFilter }} orders right now</p>
          </div>

          <!-- Orders grid -->
          <div v-else class="orders-grid">
            <div
              v-for="order in filteredOrders"
              :key="order.id"
              :class="['order-card', 'card-' + order.order_status]"
            >
              <!-- Card header -->
              <div class="card-head">
                <div class="card-head-left">
                  <span class="order-num">#{{ shortNum(order.order_number) }}</span>
                  <span :class="['elapsed', elapsedClass(order.placed_at)]">
                    {{ timeAgo(order.placed_at) }}
                  </span>
                </div>
                <span :class="['type-chip', order.order_type]">
                  {{ order.order_type === 'delivery' ? '🛵 Delivery' : '🏃 Pickup' }}
                </span>
              </div>

              <!-- Customer -->
              <div class="customer-block">
                <span class="customer-name">{{ order.customer_name || 'Customer' }}</span>
                <a v-if="order.customer_phone" :href="'tel:' + order.customer_phone" class="customer-phone">
                  {{ order.customer_phone }}
                </a>
              </div>
              <div v-if="order.delivery_address || (order.delivery_lat && order.delivery_lng)" class="address-line">
                📍 {{ order.delivery_address || '' }}
                <a
                  v-if="order.delivery_lat && order.delivery_lng"
                  :href="`https://maps.google.com/?q=${order.delivery_lat},${order.delivery_lng}`"
                  target="_blank"
                  class="map-link"
                >🗺 Map</a>
              </div>

              <!-- Items -->
              <div class="items-block">
                <div v-for="(item, i) in parseItems(order.items)" :key="i" class="item-row">
                  <span class="item-name">{{ item.name }}</span>
                  <span class="item-meta">×{{ item.quantity }}  ·  {{ (item.price * item.quantity).toFixed(0) }} EGP</span>
                </div>
              </div>

              <!-- Footer: total + rider + status chip -->
              <div class="card-foot">
                <span class="total-amount">{{ Number(order.total).toFixed(0) }} EGP</span>
                <span v-if="order.rider_name" class="rider-chip">🛵 {{ order.rider_name }}</span>
                <span :class="['status-chip', 'chip-' + order.order_status]">
                  {{ getStatusLabel(order.order_status) }}
                </span>
              </div>

              <!-- Rating -->
              <div v-if="order.rating" class="rating-row">
                <span class="order-stars">{{ '★'.repeat(Number(order.rating)) }}{{ '☆'.repeat(5 - Number(order.rating)) }}</span>
                <span v-if="order.rating_comment" class="rating-comment">"{{ order.rating_comment }}"</span>
              </div>

              <!-- Actions -->
              <div class="action-row">
                <button
                  v-if="nextStatus(order.order_status)"
                  :class="['btn-advance', 'adv-' + order.order_status]"
                  :disabled="order._updating"
                  @click="advanceStatus(order)"
                >
                  {{ nextLabel(order.order_status) }}
                </button>
                <button
                  v-if="!['delivered','cancelled'].includes(order.order_status)"
                  class="btn-cancel"
                  :disabled="order._updating"
                  @click="cancelOrder(order)"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Toast -->
        <transition name="toast-slide">
          <div v-if="toast.show" :class="['toast', 'toast-' + toast.type]">
            {{ toast.message }}
          </div>
        </transition>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'

const STATUS_FLOW = {
  pending: 'confirmed',
  confirmed: 'preparing',
  preparing: 'ready',
  ready: 'out_for_delivery',
  out_for_delivery: 'delivered'
}

const NEXT_LABELS = {
  pending: '✅ Confirm Order',
  confirmed: '👨‍🍳 Start Preparing',
  preparing: '✅ Mark Ready',
  ready: '🚚 Out for Delivery',
  out_for_delivery: '✅ Delivered'
}

const STATUS_LABELS = {
  pending: '⏳ Pending',
  confirmed: '✅ Confirmed',
  preparing: '👨‍🍳 Preparing',
  ready: '🔔 Ready',
  out_for_delivery: '🚚 Delivering',
  delivered: '✅ Delivered',
  cancelled: '❌ Cancelled'
}

export default {
  name: 'RestaurantOrdersView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      loading: false,
      orders: [],
      orderFilter: 'active',
      refreshPct: 100,
      refreshInterval: null,
      tickInterval: null,
      toast: { show: false, message: '', type: 'success' },
      statusTabs: [
        { value: 'active', label: 'Active', icon: '🔥' },
        { value: 'all', label: 'All', icon: '📋' },
        { value: 'pending', label: 'Pending', icon: '⏳' },
        { value: 'confirmed', label: 'Confirmed', icon: '✅' },
        { value: 'preparing', label: 'Preparing', icon: '👨‍🍳' },
        { value: 'ready', label: 'Ready', icon: '🔔' },
        { value: 'out_for_delivery', label: 'Delivering', icon: '🚚' },
        { value: 'delivered', label: 'Delivered', icon: '📦' },
        { value: 'cancelled', label: 'Cancelled', icon: '❌' }
      ]
    }
  },
  computed: {
    filteredOrders () {
      if (this.orderFilter === 'active') {
        return this.orders.filter(o => !['delivered', 'cancelled'].includes(o.order_status))
      }
      if (this.orderFilter === 'all') return this.orders
      return this.orders.filter(o => o.order_status === this.orderFilter)
    }
  },
  async mounted () {
    await this.loadOrders()
    if (!this._unmounted) this.startRefresh()
  },
  beforeUnmount () {
    this._unmounted = true
    clearInterval(this.refreshInterval)
    clearInterval(this.tickInterval)
  },
  methods: {
    startRefresh () {
      const INTERVAL = 30
      let remaining = INTERVAL
      this.tickInterval = setInterval(() => {
        remaining--
        this.refreshPct = (remaining / INTERVAL) * 100
        if (remaining <= 0) {
          remaining = INTERVAL
          this.loadOrders()
        }
      }, 1000)
    },

    async loadOrders () {
      if (this._unmounted) return
      try {
        this.loading = true
        const token = localStorage.getItem('auth_token')
        const res = await fetch('https://api.quali-d.com/restaurant-mode/orders', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data.success) {
          // Preserve _updating flag for in-flight orders
          const prev = {}
          this.orders.forEach(o => { prev[o.id] = o._updating })
          this.orders = (data.orders || []).map(o => ({
            ...o,
            _updating: prev[o.id] || false
          }))
        }
      } catch (e) {
        this.showToast('Failed to load orders', 'error')
      } finally {
        this.loading = false
        this.refreshPct = 100
      }
    },

    async advanceStatus (order) {
      const next = STATUS_FLOW[order.order_status]
      if (!next) return
      this.setUpdating(order.id, true)
      const prevStatus = order.order_status
      order.order_status = next
      try {
        const token = localStorage.getItem('auth_token')
        const res = await fetch(`https://api.quali-d.com/restaurant-mode/orders/${order.id}/status`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_status: next })
        })
        const data = await res.json()
        if (!data.success) {
          order.order_status = prevStatus
          this.showToast(data.message || 'Failed to update status', 'error')
        } else {
          this.showToast('Order updated → ' + STATUS_LABELS[next], 'success')
        }
      } catch {
        order.order_status = prevStatus
        this.showToast('Network error', 'error')
      } finally {
        this.setUpdating(order.id, false)
      }
    },

    async cancelOrder (order) {
      if (!confirm(`Cancel order ${order.order_number}?`)) return
      this.setUpdating(order.id, true)
      const prevStatus = order.order_status
      order.order_status = 'cancelled'
      try {
        const token = localStorage.getItem('auth_token')
        const res = await fetch(`https://api.quali-d.com/restaurant-mode/orders/${order.id}/cancel`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (!data.success) {
          order.order_status = prevStatus
          this.showToast(data.message || 'Failed to cancel', 'error')
        } else {
          this.showToast('Order cancelled', 'warning')
        }
      } catch {
        order.order_status = prevStatus
        this.showToast('Network error', 'error')
      } finally {
        this.setUpdating(order.id, false)
      }
    },

    setUpdating (id, val) {
      const o = this.orders.find(x => x.id === id)
      if (o) o._updating = val
    },

    showToast (message, type) {
      this.toast = { show: true, message, type }
      setTimeout(() => { this.toast.show = false }, 3000)
    },

    countByStatus (status) {
      if (status === 'active') return this.orders.filter(o => !['delivered', 'cancelled'].includes(o.order_status)).length
      return this.orders.filter(o => o.order_status === status).length
    },

    nextStatus (status) { return STATUS_FLOW[status] || null },
    nextLabel (status) { return NEXT_LABELS[status] || '' },
    getStatusLabel (status) { return STATUS_LABELS[status] || status },

    parseItems (json) {
      try { return JSON.parse(json) } catch { return [] }
    },

    shortNum (num) {
      if (!num) return '—'
      return num.split('-').pop() || num
    },

    timeAgo (dateStr) {
      if (!dateStr) return ''
      const mins = Math.floor((Date.now() - new Date(dateStr)) / 60000)
      if (mins < 1) return 'just now'
      if (mins < 60) return `${mins}m ago`
      return `${Math.floor(mins / 60)}h ${mins % 60}m ago`
    },

    elapsedClass (dateStr) {
      if (!dateStr) return ''
      const mins = Math.floor((Date.now() - new Date(dateStr)) / 60000)
      if (mins < 15) return 'elapsed-ok'
      if (mins < 30) return 'elapsed-warn'
      return 'elapsed-late'
    }
  }
}
</script>

<style scoped>
/* ── Layout ── */
.orders-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
  min-width: 0;
}
.page-content {
  flex: 1;
  padding: 20px 24px 40px;
  overflow-x: hidden;
}

/* ── Refresh bar ── */
.refresh-track {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--border-primary);
  z-index: 200;
}
.refresh-fill {
  height: 100%;
  background: #6366f1;
  transition: width 1s linear;
}

/* ── Top row ── */
.orders-container { max-width: 1600px; margin: 0 auto; }
.top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.title-block { display: flex; align-items: center; gap: 10px; }
.page-title { margin: 0; font-size: 24px; font-weight: 800; color: var(--text-primary); }
.live-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 0 rgba(34,197,94,.4);
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(34,197,94,.4); }
  70% { box-shadow: 0 0 0 8px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}
.order-count {
  background: #6366f1;
  color: #fff;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 13px;
  font-weight: 700;
}
.refresh-btn {
  width: 40px; height: 40px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 20px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all .2s;
}
.refresh-btn:hover { color: #6366f1; border-color: #6366f1; }
.refresh-btn.spinning { animation: spin .6s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Filter pills ── */
.filter-row {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 20px;
  scrollbar-width: none;
}
.filter-row::-webkit-scrollbar { display: none; }
.pill {
  display: flex; align-items: center; gap: 5px;
  padding: 8px 14px;
  border: 1.5px solid var(--border-primary);
  border-radius: 20px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 13px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  transition: all .15s;
}
.pill:hover { border-color: #6366f1; color: #6366f1; }
.pill.active { background: #6366f1; border-color: #6366f1; color: #fff; }
.pill-count {
  background: rgba(255,255,255,.3);
  border-radius: 10px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 700;
}
.pill:not(.active) .pill-count {
  background: #fee2e2;
  color: #dc2626;
}

/* ── States ── */
.state-block {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 80px 0; color: var(--text-secondary);
  gap: 12px;
}
.spinner {
  width: 36px; height: 36px;
  border: 3px solid var(--border-primary);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
.empty-icon { font-size: 48px; }

/* ── Grid ── */
.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 14px;
}

/* ── Order card ── */
.order-card {
  background: var(--bg-primary);
  border-radius: 14px;
  border: 1.5px solid var(--border-primary);
  border-left: 5px solid #e5e7eb;
  padding: 14px 16px;
  display: flex; flex-direction: column; gap: 10px;
  transition: box-shadow .2s;
}
.order-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.08); }

/* Left border accent by status */
.card-pending       { border-left-color: #f59e0b; }
.card-confirmed     { border-left-color: #3b82f6; }
.card-preparing     { border-left-color: #8b5cf6; }
.card-ready         { border-left-color: #22c55e; }
.card-out_for_delivery { border-left-color: #6366f1; }
.card-delivered     { border-left-color: #10b981; opacity: .7; }
.card-cancelled     { border-left-color: #ef4444; opacity: .6; }

/* Card head */
.card-head {
  display: flex; justify-content: space-between; align-items: center;
}
.card-head-left { display: flex; align-items: center; gap: 8px; }
.order-num { font-size: 15px; font-weight: 800; color: var(--text-primary); }
.elapsed {
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 10px;
}
.elapsed-ok   { background: #dcfce7; color: #15803d; }
.elapsed-warn { background: #fef3c7; color: #b45309; }
.elapsed-late { background: #fee2e2; color: #dc2626; }

.type-chip {
  font-size: 11px; font-weight: 700; padding: 3px 8px;
  border-radius: 8px;
}
.type-chip.delivery { background: #ede9fe; color: #6d28d9; }
.type-chip.pickup   { background: #dbeafe; color: #1d4ed8; }

/* Customer */
.customer-block {
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
}
.customer-name { font-weight: 700; color: var(--text-primary); font-size: 14px; }
.customer-phone { font-size: 12px; color: #6366f1; text-decoration: none; }
.customer-phone:hover { text-decoration: underline; }

.address-line {
  font-size: 12px; color: var(--text-secondary);
  display: flex; align-items: center; gap: 6px;
  overflow: hidden;
}
.map-link {
  flex-shrink: 0; font-size: 11px; font-weight: 700;
  color: #6366f1; text-decoration: none;
  padding: 1px 6px; border-radius: 6px; background: #eef2ff;
}
.map-link:hover { background: #e0e7ff; }

/* Items */
.items-block {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 8px 10px;
  display: flex; flex-direction: column; gap: 4px;
}
.item-row { display: flex; justify-content: space-between; align-items: center; }
.item-name { font-size: 13px; color: var(--text-primary); font-weight: 500; }
.item-meta { font-size: 12px; color: var(--text-secondary); white-space: nowrap; }

/* Footer */
.card-foot { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.total-amount { font-size: 16px; font-weight: 800; color: var(--text-primary); flex: 1; }
.rider-chip {
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 8px; background: #fef3c7; color: #92400e;
}
.status-chip {
  font-size: 11px; font-weight: 700; padding: 3px 10px;
  border-radius: 10px; background: var(--bg-secondary); color: var(--text-secondary);
}
.chip-pending           { background: #fef3c7; color: #b45309; }
.chip-confirmed         { background: #dbeafe; color: #1d4ed8; }
.chip-preparing         { background: #ede9fe; color: #6d28d9; }
.chip-ready             { background: #dcfce7; color: #15803d; }
.chip-out_for_delivery  { background: #e0e7ff; color: #4338ca; }
.chip-delivered         { background: #d1fae5; color: #065f46; }
.chip-cancelled         { background: #fee2e2; color: #991b1b; }

/* Rating */
.rating-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-top: 1px solid var(--border-primary); }
.order-stars { color: #f59e0b; font-size: 14px; letter-spacing: 1px; }
.rating-comment { font-size: 12px; color: var(--text-secondary); font-style: italic; }

/* Actions */
.action-row { display: flex; gap: 8px; }
.btn-advance {
  flex: 1;
  padding: 11px 0;
  border: none; border-radius: 10px;
  font-size: 13px; font-weight: 700;
  cursor: pointer; transition: opacity .15s, transform .1s;
  background: #6366f1; color: #fff;
}
.btn-advance:active { transform: scale(.97); }
.btn-advance:disabled { opacity: .5; cursor: default; }

/* Per-status colors for advance button */
.adv-pending       { background: #22c55e; }
.adv-confirmed     { background: #8b5cf6; }
.adv-preparing     { background: #22c55e; }
.adv-ready         { background: #6366f1; }
.adv-out_for_delivery { background: #10b981; }

.btn-cancel {
  width: 40px; height: 40px;
  border: 1.5px solid #fecaca;
  border-radius: 10px;
  background: #fff1f2;
  color: #ef4444;
  font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all .15s;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.btn-cancel:hover { background: #fee2e2; border-color: #ef4444; }
.btn-cancel:disabled { opacity: .4; cursor: default; }

/* ── Toast ── */
.toast {
  position: fixed; bottom: 28px; right: 28px;
  padding: 14px 22px; border-radius: 12px;
  font-size: 14px; font-weight: 600;
  color: #fff; z-index: 9999;
  box-shadow: 0 8px 24px rgba(0,0,0,.18);
  max-width: 340px;
}
.toast-success { background: #10b981; }
.toast-error   { background: #ef4444; }
.toast-warning { background: #f59e0b; }
.toast-slide-enter-active { animation: slideUp .25s ease; }
.toast-slide-leave-active { animation: slideUp .2s ease reverse; }
@keyframes slideUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Dark mode ── */
@media (prefers-color-scheme: dark) {
  .order-card { border-color: #374151; }
  .items-block { background: #1f2937; }
  .btn-cancel { background: #1f1315; border-color: #7f1d1d; color: #f87171; }
  .btn-cancel:hover { background: #2d1515; border-color: #ef4444; }
  .elapsed-ok   { background: #064e3b; color: #6ee7b7; }
  .elapsed-warn { background: #451a03; color: #fcd34d; }
  .elapsed-late { background: #450a0a; color: #fca5a5; }
  .type-chip.delivery { background: #2e1065; color: #c4b5fd; }
  .type-chip.pickup   { background: #1e3a5f; color: #93c5fd; }
  .chip-pending           { background: #451a03; color: #fcd34d; }
  .chip-confirmed         { background: #1e3a5f; color: #93c5fd; }
  .chip-preparing         { background: #2e1065; color: #c4b5fd; }
  .chip-ready             { background: #064e3b; color: #6ee7b7; }
  .chip-out_for_delivery  { background: #1e1b4b; color: #a5b4fc; }
  .chip-delivered         { background: #022c22; color: #34d399; }
  .chip-cancelled         { background: #450a0a; color: #fca5a5; }
  .rider-chip { background: #451a03; color: #fcd34d; }
  .pill:not(.active) .pill-count { background: #450a0a; color: #f87171; }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .main-content { margin-left: 0; padding-top: 70px; }
  .page-content { padding: 12px 12px 40px; }
  .orders-grid { grid-template-columns: 1fr; }
  .filter-row { gap: 6px; }
}
</style>
